import { SupabaseClient } from '@supabase/supabase-js';
import { supabase as supabaseClient } from '../client/browser';

export interface PromoCode {
  code: string;
  description: string;
  discountType: 'PERCENTAGE' | 'FIXED' | 'FREE_SESSIONS';
  discountValue: number; // For percentage: 0-100, for fixed: dollar amount, for free sessions: number of sessions
  validFrom?: Date;
  validUntil?: Date;
  applicableTiers?: string[]; // Which therapist tiers this applies to
  maxUses?: number;
  currentUses?: number;
  isActive: boolean;
}


/**
 * Validate a promo code from database
 */
export async function validatePromoCode(
  code: string,
  therapistTier?: string,
  client?: SupabaseClient
): Promise<PromoCode | null> {
  if (!code) return null;
  
  const upperCode = code.toUpperCase().trim();
  const supabase = client || supabaseClient;
  
  try {
    // Query the promos table in the database
    const { data: promoData, error } = await supabase
      .from('promos')
      .select('*')
      .eq('code', upperCode)
      .single();
    
    if (error || !promoData) {
      console.log('[PromoService] Promo code not found in database:', upperCode);
      return null;
    }
    
    // Map database fields to PromoCode interface
    const promo: PromoCode = {
      code: promoData.code,
      description: promoData.description || '',
      discountType: promoData.discountType || promoData.discount_type || 'PERCENTAGE',
      discountValue: promoData.discountValue || promoData.discount_value || 0,
      validFrom: promoData.validFrom || promoData.valid_from ? new Date(promoData.validFrom || promoData.valid_from) : undefined,
      validUntil: promoData.validUntil || promoData.valid_until ? new Date(promoData.validUntil || promoData.valid_until) : undefined,
      applicableTiers: promoData.tier ? [promoData.tier.toUpperCase()] : [],
      isActive: promoData.isActive !== undefined ? promoData.isActive : (promoData.is_active !== undefined ? promoData.is_active : true)
    };
    
    // Check if promo is active
    if (!promo.isActive) {
      console.log('[PromoService] Promo code is inactive:', upperCode);
      return null;
    }
    
    // Check validity dates
    const now = new Date();
    if (promo.validFrom && now < promo.validFrom) {
      console.log('[PromoService] Promo code not yet valid:', upperCode);
      return null;
    }
    if (promo.validUntil && now > promo.validUntil) {
      console.log('[PromoService] Promo code expired:', upperCode);
      return null;
    }
    
    // Check applicable tiers
    // If promo has no tier restriction (empty array), it applies to all tiers
    if (promo.applicableTiers && promo.applicableTiers.length > 0) {
      const promoTierUpper = promo.applicableTiers[0]?.toUpperCase();
      
      // If promo tier is 'ALL', it applies to everyone
      if (promoTierUpper === 'ALL') {
        console.log('[PromoService] Promo code applies to all tiers:', upperCode);
      } else if (therapistTier) {
        // Check if therapist tier matches promo tier
        const tierUpper = therapistTier.toUpperCase();
        
        // Handle intern/resident equivalence (they're the same tier)
        const normalizedTherapistTier = tierUpper === 'INTERN' ? 'RESIDENT' : tierUpper;
        const normalizedPromoTier = promoTierUpper === 'INTERN' ? 'RESIDENT' : promoTierUpper;
        
        if (normalizedPromoTier !== normalizedTherapistTier) {
          console.log('[PromoService] Promo code not applicable for tier:', { 
            code: upperCode, 
            therapistTier: normalizedTherapistTier,
            promoTier: normalizedPromoTier 
          });
          return null;
        }
        console.log('[PromoService] Promo code tier matches:', { 
          code: upperCode, 
          therapistTier: normalizedTherapistTier,
          promoTier: normalizedPromoTier 
        });
      }
    } else {
      // No tier restriction means it applies to all tiers
      console.log('[PromoService] Promo code has no tier restriction, applies to all:', upperCode);
    }
    
    console.log('[PromoService] Promo code validated successfully from database:', { code: upperCode, promo });
    return promo;
  } catch (err) {
    console.error('[PromoService] Error validating promo code:', err);
    return null;
  }
}


/**
 * Calculate discounted rate based on promo code
 */
export function calculateDiscountedRate(
  originalRate: number,
  promo: PromoCode,
  sessionNumber: number = 1
): number {
  switch (promo.discountType) {
    case 'FREE_SESSIONS':
      // If this is within the free sessions range, return 0
      if (sessionNumber <= promo.discountValue) {
        return 0;
      }
      return originalRate;
      
    case 'PERCENTAGE':
      const discountAmount = (originalRate * promo.discountValue) / 100;
      return Math.max(0, originalRate - discountAmount);
      
    case 'FIXED':
      return Math.max(0, originalRate - promo.discountValue);
      
    default:
      return originalRate;
  }
}

/**
 * Get promo code by code (without validation)
 */
export async function getPromoCode(
  code: string,
  client?: SupabaseClient
): Promise<PromoCode | null> {
  if (!code) return null;
  
  const upperCode = code.toUpperCase().trim();
  const supabase = client || supabaseClient;
  
  try {
    // Query the promos table in the database
    const { data: promoData, error } = await supabase
      .from('promos')
      .select('*')
      .eq('code', upperCode)
      .single();
    
    if (error || !promoData) {
      return null;
    }
    
    // Map database fields to PromoCode interface
    const promo: PromoCode = {
      code: promoData.code,
      description: promoData.description || '',
      discountType: promoData.discountType || promoData.discount_type || 'PERCENTAGE',
      discountValue: promoData.discountValue || promoData.discount_value || 0,
      validFrom: promoData.validFrom || promoData.valid_from ? new Date(promoData.validFrom || promoData.valid_from) : undefined,
      validUntil: promoData.validUntil || promoData.valid_until ? new Date(promoData.validUntil || promoData.valid_until) : undefined,
      applicableTiers: promoData.tier ? [promoData.tier.toUpperCase()] : [],
      isActive: promoData.isActive !== undefined ? promoData.isActive : (promoData.is_active !== undefined ? promoData.is_active : true)
    };
    
    return promo;
  } catch (err) {
    console.error('[PromoService] Error getting promo code:', err);
    return null;
  }
}

/**
 * Check if a promo code provides free sessions
 */
export function isFreeSessiosPromo(promo: PromoCode | null): boolean {
  return promo?.discountType === 'FREE_SESSIONS';
}

/**
 * Get promo code display text
 */
export function getPromoDisplayText(promo: PromoCode): string {
  switch (promo.discountType) {
    case 'FREE_SESSIONS':
      const sessions = promo.discountValue === 1 ? 'session' : 'sessions';
      return `${promo.discountValue} free ${sessions}`;
      
    case 'PERCENTAGE':
      return `${promo.discountValue}% off`;
      
    case 'FIXED':
      return `$${promo.discountValue} off`;
      
    default:
      return promo.description;
  }
}